import { useMutation, UseMutationOptions } from "@tanstack/react-query";

import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { SKIP_SENTRY_FOR_CLIENT_ERRORS } from "lib/sentryErrors";

export type ChangePasswordVariables = {
  currentPassword: string;
  newPassword: string;
  revokeOtherSessions: boolean;
};

/** A refused password change, carrying better-auth's error `code` when the server sent one. */
export class ChangePasswordError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
  }
}

export function useChangePassword(
  options?: UseMutationOptions<unknown, ChangePasswordError, ChangePasswordVariables, unknown>,
) {
  return useMutation({
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    mutationKey: ["changePassword"],
    mutationFn: async (variables: ChangePasswordVariables) => {
      const { data, error } = await authClient.changePassword(variables);
      if (error) {
        throw new ChangePasswordError(
          betterAuthErrorText(error, "Could not change your password"),
          error.code,
          { cause: error },
        );
      }
      return data;
    },
    ...options,
  });
}
